import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import { DEFAULT_ASSIGNED_USE_CASE_IDS } from '../../tools/constants/assignableUseCases';
import { ToolUseCaseAssignment } from '../../tools/components/ToolUseCaseAssignment';
import extraStyles from '../../tools/components/AgentExtras.module.css';
import pluginStyles from '../../ai-tool-plugins/components/ToolPluginsPage.module.css';
import {
  AlertIcon,
  CheckIcon,
  CloseIcon,
  LayersIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from '../../ai-tool-plugins/components/ToolPluginIcons';
import { createAiHook, deleteAiHook, listAiHooks, updateAiHook } from '../services/hookService';
import type { AiHookActionType, AiHookDefinition, AiHookEvent } from '../types/Hook';
import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';

type DrawerMode = 'create' | 'edit' | null;
type StatusFilter = 'all' | 'published' | 'draft';
type FormState = {
  hookId: string;
  name: string;
  event: AiHookEvent;
  actionType: AiHookActionType;
  url: string;
  secret: string;
  timeoutSeconds: number;
  promptSnippet: string;
  failOpen: boolean;
  isPublished: boolean;
  assignedUseCaseIds: AiPromptUseCaseId[];
  toolMatcher: string;
};

const emptyForm = (): FormState => ({
  hookId: '',
  name: '',
  event: 'beforeReply',
  actionType: 'http',
  url: '',
  secret: '',
  timeoutSeconds: 3,
  promptSnippet: '',
  failOpen: true,
  isPublished: false,
  assignedUseCaseIds: [...DEFAULT_ASSIGNED_USE_CASE_IDS],
  toolMatcher: '',
});

export const HooksPage: React.FC = () => {
  const { t } = useTranslation('agentHooks');
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission([PermissionCodes.aiConfigManage]);
  const [items, setItems] = useState<AiHookDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formIssue, setFormIssue] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listAiHooks());
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const visibleItems = useMemo(() => {
    const search = query.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter === 'published' && !item.isPublished) return false;
      if (statusFilter === 'draft' && item.isPublished) return false;
      if (!search) return true;
      return item.name.toLowerCase().includes(search)
        || item.event.toLowerCase().includes(search)
        || (item.url ?? '').toLowerCase().includes(search);
    });
  }, [items, query, statusFilter]);

  const openCreate = () => {
    setForm(emptyForm());
    setFormIssue(null);
    setDrawerMode('create');
  };

  const openEdit = (item: AiHookDefinition) => {
    setForm({
      hookId: item.hookId,
      name: item.name,
      event: item.event,
      actionType: item.actionType,
      url: item.url ?? '',
      secret: '',
      timeoutSeconds: item.timeoutSeconds,
      promptSnippet: item.promptSnippet ?? '',
      failOpen: item.failOpen,
      isPublished: item.isPublished,
      assignedUseCaseIds: [...item.assignedUseCaseIds],
      toolMatcher: item.toolMatcher ?? '',
    });
    setFormIssue(null);
    setDrawerMode('edit');
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return 'nameRequired';
    if (form.actionType === 'http' && !form.url.trim()) return 'urlRequired';
    if (form.actionType === 'prompt' && !form.promptSnippet.trim()) return 'promptRequired';
    return null;
  };

  const handleSave = async () => {
    if (!canManage) return;
    const issue = validate();
    if (issue) {
      setFormIssue(issue);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        event: form.event,
        actionType: form.actionType,
        url: form.actionType === 'http' ? form.url.trim() : null,
        secret: form.secret.trim() || null,
        timeoutSeconds: form.timeoutSeconds,
        promptSnippet: form.actionType === 'prompt' ? form.promptSnippet.trim() : null,
        failOpen: form.failOpen,
        isPublished: form.isPublished,
        assignedUseCaseIds: form.assignedUseCaseIds,
        toolMatcher: form.toolMatcher.trim() || null,
      };
      if (drawerMode === 'edit') {
        await updateAiHook(form.hookId, payload);
        showToast(t('updated'), 'success');
      } else {
        await createAiHook({ ...payload, hookId: form.hookId.trim() || undefined });
        showToast(t('created'), 'success');
      }
      setDrawerMode(null);
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: AiHookDefinition) => {
    if (!canManage) return;
    if (!window.confirm(t('confirmDelete', { name: item.name }))) return;
    try {
      await deleteAiHook(item.hookId);
      showToast(t('deleted'), 'success');
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDelete'), 'error');
    }
  };

  const eventLabel = (event: AiHookEvent) => {
    if (event === 'afterToolUse') return t('eventAfterToolUse');
    if (event === 'onHumanHandoff') return t('eventOnHumanHandoff');
    return t('eventBeforeReply');
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className={`${pluginStyles.page} ${extraStyles.page}`}>
      <div className={pluginStyles.pageBar}>
        <div className={pluginStyles.statStrip}>
          <span className={pluginStyles.stat}>
            <span className={pluginStyles.statValue}>{items.length}</span>
            {t('statItems')}
          </span>
          <span className={pluginStyles.stat}>
            <span className={pluginStyles.statValue}>{items.filter((item) => item.isPublished).length}</span>
            {t('statPublished')}
          </span>
        </div>
        {canManage ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <PlusIcon />
            <span>{t('create')}</span>
          </button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <div className={pluginStyles.empty}>
          <span className={pluginStyles.emptyIcon}><LayersIcon size={22} /></span>
          <span className={pluginStyles.emptyTitle}>{t('emptyTitle')}</span>
          <p className={pluginStyles.emptyText}>{t('emptyText')}</p>
          {canManage ? (
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              <PlusIcon />
              <span>{t('create')}</span>
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className={pluginStyles.listToolbar}>
            <div className={`${pluginStyles.searchField} ${pluginStyles.listToolbarSearch}`}>
              <SearchIcon className={pluginStyles.searchFieldIcon} size={15} />
              <input
                className={pluginStyles.searchInput}
                value={query}
                placeholder={t('search')}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <div className={pluginStyles.segmented}>
              {(['all', 'published', 'draft'] as StatusFilter[]).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`${pluginStyles.segment} ${statusFilter === id ? pluginStyles.segmentActive : ''}`}
                  onClick={() => setStatusFilter(id)}
                >
                  {t(id === 'all' ? 'filterAll' : id === 'published' ? 'filterPublished' : 'filterDraft')}
                </button>
              ))}
            </div>
          </div>
          {visibleItems.length === 0 ? (
            <p className={pluginStyles.emptyText}>{t('noSearchResults')}</p>
          ) : (
            <div className={pluginStyles.pluginGrid}>
              {visibleItems.map((item) => (
                <article
                  key={item.hookId}
                  className={`${pluginStyles.pluginCard} ${item.isPublished ? pluginStyles.pluginCardPublished : ''}`}
                >
                  <div className={pluginStyles.pluginCardTop}>
                    <span className={pluginStyles.pluginName}>{item.name}</span>
                    <span className={`${pluginStyles.badge} ${item.isPublished ? pluginStyles.badgeSuccess : pluginStyles.badgeWarn}`}>
                      {item.isPublished ? <CheckIcon size={12} /> : null}
                      {item.isPublished ? t('published') : t('draft')}
                    </span>
                  </div>
                  <div className={pluginStyles.pluginBadges}>
                    <span className={pluginStyles.badge}>{eventLabel(item.event)}</span>
                    <span className={pluginStyles.badge}>
                      {item.actionType === 'http' ? t('actionHttp') : t('actionPrompt')}
                    </span>
                  </div>
                  <div className={pluginStyles.pluginCardActions}>
                    <button type="button" className={pluginStyles.iconButton} onClick={() => openEdit(item)}>
                      <PencilIcon size={15} />
                    </button>
                    {canManage ? (
                      <button
                        type="button"
                        className={`${pluginStyles.iconButton} ${pluginStyles.iconButtonDanger}`}
                        onClick={() => void handleDelete(item)}
                      >
                        <TrashIcon size={15} />
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {drawerMode ? (
        <div className={pluginStyles.drawerOverlay} onClick={() => !saving && setDrawerMode(null)}>
          <div className={pluginStyles.drawer} role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className={pluginStyles.drawerHead}>
              <div>
                <h3 className={pluginStyles.drawerTitle}>{drawerMode === 'edit' ? t('edit') : t('create')}</h3>
                <p className={pluginStyles.drawerSubtitle}>{t('formHelp')}</p>
              </div>
              <button type="button" className={pluginStyles.iconButton} onClick={() => setDrawerMode(null)}>
                <CloseIcon size={15} />
              </button>
            </div>
            <div className={pluginStyles.drawerBody}>
              {formIssue ? (
                <div className={pluginStyles.formError}>
                  <AlertIcon size={15} />
                  <span>{t(`issue_${formIssue}`)}</span>
                </div>
              ) : null}
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('name')}</span>
                <input
                  className="text-input"
                  value={form.name}
                  placeholder={t('namePlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                />
              </label>
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('event')}</span>
                <select
                  className={`text-input ${extraStyles.select}`}
                  value={form.event}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, event: event.target.value as AiHookEvent }))}
                >
                  <option value="beforeReply">{t('eventBeforeReply')}</option>
                  <option value="afterToolUse">{t('eventAfterToolUse')}</option>
                  <option value="onHumanHandoff">{t('eventOnHumanHandoff')}</option>
                </select>
              </label>
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('actionType')}</span>
                <select
                  className={`text-input ${extraStyles.select}`}
                  value={form.actionType}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, actionType: event.target.value as AiHookActionType }))}
                >
                  <option value="http">{t('actionHttp')}</option>
                  <option value="prompt">{t('actionPrompt')}</option>
                </select>
              </label>
              {form.actionType === 'http' ? (
                <>
                  <label className={pluginStyles.field}>
                    <span className={pluginStyles.fieldLabel}>{t('url')}</span>
                    <input
                      className="text-input"
                      value={form.url}
                      placeholder={t('urlPlaceholder')}
                      disabled={!canManage || saving}
                      onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
                    />
                    <span className={pluginStyles.fieldHint}>{t('urlHint')}</span>
                  </label>
                  <label className={pluginStyles.field}>
                    <span className={pluginStyles.fieldLabel}>{t('secret')}</span>
                    <input
                      className="text-input"
                      type="password"
                      value={form.secret}
                      placeholder={t('secretPlaceholder')}
                      disabled={!canManage || saving}
                      onChange={(event) => setForm((current) => ({ ...current, secret: event.target.value }))}
                    />
                    <span className={pluginStyles.fieldHint}>{t('secretHint')}</span>
                  </label>
                  <label className={pluginStyles.field}>
                    <span className={pluginStyles.fieldLabel}>{t('timeoutSeconds')}</span>
                    <input
                      className="text-input"
                      type="number"
                      min={1}
                      max={10}
                      value={form.timeoutSeconds}
                      disabled={!canManage || saving}
                      onChange={(event) => setForm((current) => ({ ...current, timeoutSeconds: Number(event.target.value) || 3 }))}
                    />
                  </label>
                </>
              ) : (
                <label className={pluginStyles.field}>
                  <span className={pluginStyles.fieldLabel}>{t('promptSnippet')}</span>
                  <textarea
                    className={`text-input ${extraStyles.textarea}`}
                    value={form.promptSnippet}
                    placeholder={t('promptSnippetPlaceholder')}
                    disabled={!canManage || saving}
                    onChange={(event) => setForm((current) => ({ ...current, promptSnippet: event.target.value }))}
                  />
                </label>
              )}
              {form.event === 'afterToolUse' ? (
                <label className={pluginStyles.field}>
                  <span className={pluginStyles.fieldLabel}>{t('toolMatcher')}</span>
                  <input
                    className="text-input"
                    value={form.toolMatcher}
                    placeholder={t('toolMatcherPlaceholder')}
                    disabled={!canManage || saving}
                    onChange={(event) => setForm((current) => ({ ...current, toolMatcher: event.target.value }))}
                  />
                  <span className={pluginStyles.fieldHint}>{t('toolMatcherHint')}</span>
                </label>
              ) : null}
              <label className={pluginStyles.field}>
                <input
                  type="checkbox"
                  checked={form.failOpen}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, failOpen: event.target.checked }))}
                />
                <span>
                  <strong>{t('failOpen')}</strong>
                  <span className={pluginStyles.fieldHint}>{t('failOpenHelp')}</span>
                </span>
              </label>
              <label className={pluginStyles.field}>
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, isPublished: event.target.checked }))}
                />
                <span>
                  <strong>{t('publish')}</strong>
                  <span className={pluginStyles.fieldHint}>{t('publishHelp')}</span>
                </span>
              </label>
              <ToolUseCaseAssignment
                selectedUseCaseIds={form.assignedUseCaseIds}
                isPublished={form.isPublished}
                disabled={!canManage || saving}
                onChange={(assignedUseCaseIds) => setForm((current) => ({ ...current, assignedUseCaseIds }))}
              />
            </div>
            {canManage ? (
              <div className={pluginStyles.drawerFoot}>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void handleSave()}>
                  {saving ? t('saving') : t('save')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HooksPage;
